/**
 * domains/learning/snapshotV2Runtime.ts
 *
 * Phase 4B -- Immutable Snapshot v2: runtime wrapper (IMPURE I/O bridge).
 *
 * This is the ONLY file in the Phase 4B client path that imports the heavy
 * runtime deps -- the Zustand store, the thesis stores, and the divergence
 * runtime. It resolves the active symbol + the thesis/divergence read for this
 * symbol, hands them to the pure assembler (snapshotV2Core.buildCaptureBody),
 * and fire-and-forgets the resulting body via postCaptureBody.
 *
 * It exists so that snapshotV2Core stays pure and bare-node-loadable for tests:
 * all the un-Node-loadable imports live here, in code that is never unit-tested
 * (pure wiring / I/O), exactly mirroring the attribution split
 * (attributionRuntime bridge over the pure processor).
 *
 * GUARANTEES (this function is called from the getSymbolContext cache-miss seam,
 * beside captureFromContext, so it must behave exactly like that precedent):
 *   - returns void; never throws (single try/catch swallow);
 *   - never mutates ctx (only reads; core reuses references, never edits);
 *   - never blocks the selector / evaluation path (no await; postCaptureBody is
 *     fire-and-forget);
 *   - server remains the sole capture authority (no client dedupe here).
 */

import { useStore } from '../../shared/store';
import { getThesis2 } from '../ai/thesis2/store';
import { getThesis } from '../ai/thesis/thesisEngine';
import { computeDivergence } from '../divergence/computeDivergence';
import { buildCaptureBody, postCaptureBody } from './snapshotV2Core';

/**
 * Best-effort auto-capture for the active symbol's freshly-computed context.
 * Drops in beside `captureFromContext(ctx as any)` at the getSymbolContext
 * cache-miss boundary; like that precedent it takes `ctx as any` and is a pure
 * side-effect on the selector layer (buildSymbolContext stays pure).
 */
export function captureSnapshotV2FromContext(ctx: any): void {
  try {
    if (!ctx || typeof ctx.symbol !== 'string') return;

    // Cheap active-symbol pre-guard (PERF only): getSymbolContext is a shared
    // selector reached from multi-symbol loops (Portfolio, dashboard scans,
    // etc.). Pre-checking here avoids the computeDivergence call + two store
    // reads for every non-active symbol. The AUTHORITATIVE guard still lives in
    // buildCaptureBody (pure + unit-tested); this never widens scope, only
    // short-circuits work that buildCaptureBody would discard anyway.
    const activeSymbol = useStore.getState().activeSymbol;
    if ((ctx.symbol || '').toUpperCase() !== (activeSymbol || '').toUpperCase()) {
      return;
    }

    const symbol: string = ctx.symbol;

    // thesis2 structured thesis (nullable) -- T7 source on the server.
    const structuredThesis = (getThesis2(symbol) && getThesis2(symbol).current) || null;

    // v1 validated AI response (nullable) -- the divergence AI input.
    const v1Thesis = (getThesis(symbol) && getThesis(symbol).current) || null;

    // Primary TradeSetup projection, 1:1 with primaryRichSetup -- the divergence
    // setup input. computeDivergence is NVS-aware and handles null on both sides.
    const setup =
      Array.isArray(ctx.setups) && ctx.setups.length > 0 ? ctx.setups[0] : null;
    const divergence = computeDivergence(v1Thesis, setup);

    const result = buildCaptureBody(ctx, {
      activeSymbol: activeSymbol,
      structuredThesis: structuredThesis,
      divergence: divergence,
      nowMs: Date.now(),
    });

    if (result.kind === 'post') {
      postCaptureBody(result.body);
    }
  } catch (_e) {
    /* never throw / never block the selector path */
  }
}
